import type { MeResponse } from '@emailforge/schemas';
import { useEffect } from 'react';

import { markInitialized, setSession, signedOut } from '@/features/auth/authSlice';
import { identify } from '@/lib/monitoring';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

export function SessionBootstrap({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const isInitialized = useAppSelector((s) => s.auth.isInitialized);
  const accessToken = useAppSelector((s) => s.auth.accessToken);

  useEffect(() => {
    if (isInitialized) return;
    if (!accessToken) {
      dispatch(markInitialized());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) {
          if (!cancelled) dispatch(signedOut());
          return;
        }
        const me = (await res.json()) as MeResponse;
        if (cancelled) return;
        dispatch(setSession({ me, accessToken }));
        identify(me.user.id, { email: me.user.email });
      } catch {
        if (!cancelled) dispatch(signedOut());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isInitialized, accessToken, dispatch]);

  if (!isInitialized) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--paper)',
          fontSize: 13,
          color: 'var(--ink-3)',
        }}
      >
        Loading…
      </div>
    );
  }

  return <>{children}</>;
}
